import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';

export interface PageHeaderProps {
  /** Uppercase eyebrow above the title, e.g. "Отчёты". */
  readonly eyebrow?: ReactNode;
  readonly title: ReactNode;
  /** Rendered next to the title (e.g. a status chip on profile pages). */
  readonly titleAdornment?: ReactNode;
  /** One-line summary under the title. */
  readonly summary?: ReactNode;
  /** Right-aligned actions (DS Buttons, size sm). */
  readonly actions?: ReactNode;
  /** A screen of the left menu: no eyebrow, the H1 for assistive tech only (V120). */
  readonly isTitleHidden?: boolean;
  readonly className?: string;
}

/** Uppercase section label: 12px / 600 / 0.06em, fg-2 (the screens' eyebrow). */
function Eyebrow({ children, className }: { readonly children: ReactNode; readonly className?: string }) {
  return (
    <div className={cn('text-[12px] font-semibold uppercase tracking-[0.06em] text-fg-2', className)}>
      {children}
    </div>
  );
}

/**
 * Screen title block shared by every page: eyebrow, h1 28px / 600 / -0.012em / 1.18, summary in
 * fg-2, actions aligned to the bottom right (design: `align-items:flex-end;gap:16px;flex-wrap`).
 */
export function PageHeader({ eyebrow, title, titleAdornment, summary, actions, isTitleHidden, className }: PageHeaderProps) {
  if (isTitleHidden) {
    return (
      <div className={cn('flex flex-wrap items-end justify-between gap-4', className)}>
        <h1 className="sr-only">{title}</h1>
        <div className="min-w-0 flex-[1_1_360px] text-fg-2">{summary}</div>
        {actions !== undefined && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
    );
  }
  return (
    <div className={cn('flex flex-wrap items-end justify-between gap-4', className)}>
      <div className="min-w-0 flex-[1_1_360px]">
        {eyebrow !== undefined && <Eyebrow>{eyebrow}</Eyebrow>}
        <div className={cn('flex items-center gap-2.5', eyebrow !== undefined && 'mt-0.5')}>
          <h1 className="m-0 text-[28px] font-semibold leading-[1.18] tracking-[-0.012em]">{title}</h1>
          {titleAdornment}
        </div>
        {summary !== undefined && <div className="mt-1 text-fg-2">{summary}</div>}
      </div>
      {actions !== undefined && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}
